import type { Catalog } from "../../en";

/** Filipino: platform admin settings screen (mirrors en). */
export const settings: Catalog["adminScreens"]["settings"] = {
  title: "Mga setting ng platform",
  subtitle: "Mga global na default para sa bawat tenant workspace. Puwedeng i-override ng tenant ang branding, retention at channels sa loob ng limit ng plan nila.",
  navLabel: "Mga seksyon ng setting",
  saved: "Na-save ang mga setting ng platform · naka-log bilang platform.settings.updated",
  notAvailable: "Hindi pa available",
  nav: {
    general: "Pangkalahatan",
    branding: "Branding",
    email: "Email",
    security: "Seguridad",
    compliance: "Data at compliance",
    flags: "Mga feature flag",
    maintenance: "Maintenance mode",
  },
  errors: {
    required: "Kailangan ang field na ito.",
    email: "Maglagay ng tamang email address.",
    fxRate: "Maglagay ng rate na higit sa 0 at hanggang 1,000.",
    hex: "Gumamit ng 6-digit na hex na kulay, hal. #00aeef.",
  },
  general: {
    subtitle: "Pagkakakilanlan at mga default para sa bagong tenant.",
    platformName: "Pangalan ng platform",
    platformNameHint: "Working name. Makikita sa mga email, sa tenant console at sa admin console.",
    operatedBy: "Pinapatakbo ng",
    supportEmail: "Support email",
    supportHotline: "Support hotline",
    region: "Default na data region",
    regionHint: "Dito nakalagay ang Postgres schema, S3 bucket at Redis keyspace ng bagong tenant.",
    timezone: "Default na timezone",
    language: "Default na wika",
    currency: "Currency",
    fxRate: "FX rate (₱ bawat US$)",
    fxRateHint: "Ginagamit para ipakita ang gastos sa provider (USD) katabi ng kita (PHP).",
    consoleUrl: "URL ng tenant console",
    copyUrl: "Kopyahin ang URL ng tenant console",
    regions: {
      ap_southeast_1: "AWS ap-southeast-1 · Singapore",
      ap_southeast_3: "AWS ap-southeast-3 · Jakarta",
      on_prem: "On-prem (Institution, CRUD.IT AI device)",
    },
    timezones: { Asia_Manila: "Asia/Manila (PHT, UTC+8)", Asia_Singapore: "Asia/Singapore", UTC: "UTC" },
    languages: { en: "English", en_fil: "English + Filipino (auto-detect, Taglish)", fil: "Filipino" },
    currencies: { PHP: "PHP (₱)", USD: "USD ($)" },
  },
  branding: {
    subtitle: "Itsura ng console, auth pages at system emails sa antas ng platform.",
    overrideLead: "Ino-override ito ng mga tenant widget.",
    overrideBody: "Bawat tenant ay may sariling kulay, avatar at pangalan ng widget sa Widget Studio. Itinatago rin ng white-label ang \"Powered by Sagot\".",
    productName: "Pangalan ng produkto",
    tagline: "Tagline",
    logo: "Logo",
    logoHint: "SVG o PNG, hindi bababa sa 256px. Awtomatikong ginagawa ang dark variant.",
    replace: "Palitan",
    primaryColor: "Pangunahing kulay",
    colorPicker: "Pumili ng pangunahing kulay",
    contrast: "Ang text sa pangunahing kulay ay may contrast na {{ratio}}:1.",
    preview: "Preview · header ng system email",
    openConsole: "Buksan ang console mo",
  },
  email: {
    senderTitle: "Nagpapadala ng email",
    senderSubtitle: "Mga system email: imbitasyon, sign-in code, alerto sa paggamit.",
    provider: "Provider",
    providers: { resend: "Resend", sendgrid: "SendGrid", ses: "Amazon SES (ap-southeast-1)", smtp: "SMTP relay" },
    from: "Mula kay",
    replyTo: "Reply-to",
    testAddress: "Test address",
    sendTest: "Magpadala ng test",
  },
  templates: {
    title: "Mga template",
    new: "Bagong template",
    template: "Template",
    channel: "Channel",
    language: "Wika",
    editLabel: "I-edit ang {{name}}",
    emptyTitle: "Wala pang message template",
    emptyBody: "Gawin ang mga email at notification na ipinapadala ng Sagot sa mga tenant at staff, sa English at Filipino.",
    newTitle: "Bagong template",
    editTitle: "I-edit ang template · {{name}}",
    name: "Pangalan",
    key: "Key",
    keyHint: "Maliliit na titik, numero, tuldok at underscore, hal. tenant.welcome",
    keyError: "Gumamit ng 2–64 na maliliit na titik, numero, tuldok o underscore, nagsisimula sa titik.",
    channelsError: "Pumili ng kahit isang channel.",
    subject: "Subject ng email",
    bodyLabel: "Mensahe sa {{channel}}",
    variables: "Mga variable:",
    sendTest: "Magpadala ng test",
    save: "I-save ang template",
    saved: "Na-save ang template",
    channels: { email: "Email", push: "Push", in_app: "In-app" },
    locales: { en: "English", fil: "Filipino" },
  },
  security: {
    staff2fa: "Ipatupad ang 2FA para sa platform staff",
    staff2faHint: "Kailangan ang TOTP o passkey. Hindi puwedeng i-off habang naka-enable ang impersonation.",
    tenant2fa: "Hingin ang 2FA sa mga tenant owner at admin",
    tenant2faHint: "Default para sa bagong tenant; puwedeng SSO ang ipatupad ng Institution tenant.",
    staff_session_hours: "Haba ng session ng staff",
    idle_timeout_minutes: "Idle timeout",
    tenant_session_days: "Haba ng session ng tenant",
    impersonation_max_minutes: "Pinakamahabang impersonation",
    units: {
      hours: "{{count}} oras",
      hours_other: "{{count}} oras",
      minutes: "{{count}} minuto",
      minutes_other: "{{count}} minuto",
      days: "{{count}} araw",
      days_other: "{{count}} araw",
    },
    allowlist: "Mga pinapayagang admin IP",
    allowlistHint: "Admin console at admin API lang. Hindi apektado ang mga tenant.",
    allowlistEmpty: "Magdagdag muna ng kahit isang IP range bago i-on ang allowlist.",
    allowlistNone: "Wala pang IP range — naaabot ang admin console mula sa kahit anong IP.",
    addTitle: "Magdagdag ng pinapayagang IP",
    cidr: "IPv4 address o CIDR",
    cidrPlaceholder: "hal. 49.145.102.7/32",
    label: "Label",
    add: "Idagdag",
    added: "Naidagdag ang {{cidr}} sa admin allowlist",
    removed: "Tinanggal ang {{cidr}} sa allowlist",
    removeIp: "Tanggalin ang {{cidr}}",
    invalidIp: "Maglagay ng tamang IPv4 address o CIDR",
  },
  compliance: {
    retentionTitle: "Mga default na retention",
    conversations: "Mga usapan",
    conversationsHint: "Pagkatapos ay buburahin; anonymized na analytics ang itatago.",
    conversationOptions: { d90: "90 araw", d180: "180 araw", d365: "1 taon", d0: "Itago (Institution)" },
    files: "Mga na-upload na file (S3)",
    fileOptions: { until_deleted: "Hanggang mabura ang source", one_year_unused: "1 taon mula huling gamit" },
    traces: "Langfuse traces",
    tracesHint: "Tinatanggal ang PII bago i-export.",
    days: "{{count}} araw",
    days_other: "{{count}} araw",
    dpaTitle: "Data Privacy Act 2012 (RA 10173)",
    npcRegistered: "Rehistrado sa NPC",
    npcNo: "NPC registration no.",
    npcNoHint: "Rehistro bilang Personal Information Controller/Processor.",
    dpoName: "Data Protection Officer",
    dpoEmail: "Email ng DPO",
    residencyLead: "Data residency:",
    residencyBody:
      "nananatili ang naka-imbak na data ng tenant sa default na data region. Ang mga prompt na ipinapadala sa external na model provider ay puwedeng iproseso sa ibang bansa sa ilalim ng zero-data-retention terms; ang tenant na kailangan ng in-country processing ay puwedeng mag-pin ng self-hosted model o ng on-prem CRUD.IT AI device. Nakasaad ito sa DPA.",
  },
  flags: {
    subtitle: "Mga global na switch at ang kanilang rollout list. Nasa page ng bawat tenant ang per-tenant override.",
    filter: "I-filter ang mga flag",
    clearFilter: "I-clear ang filter",
    stages: { beta: "Beta", experiment: "Eksperimento" },
    notWired: "Hindi pa nakakabit",
    notWiredReason: "Wala pang code na bumabasa sa flag na ito, kaya walang magbabago kapag ginalaw ito.",
    notWiredError: "Wala pang nakakabit na gamit ang flag na ito, kaya hindi ito mababago.",
    rolloutAll: "Naka-on para sa lahat ng workspace",
    rolloutNone: "Naka-off para sa lahat ng workspace",
    rolloutSome: "Rollout list: {{count}} workspace",
    rolloutSome_other: "Rollout list: {{count}} na workspace",
    enabled: "Na-enable ang {{name}}",
    disabled: "Na-disable ang {{name}}",
    emptyTitle: "Walang tugmang flag",
    emptyBody: "Subukan ang ibang pangalan o key.",
    rollout: {
      open: "Rollout list",
      openLabel: "Rollout list ng {{name}}",
      title: "Rollout list: {{name}}",
      description: "Mga workspace na nakakakuha ng feature na ito habang naka-off ang global switch. Pinapalitan ng pag-save ang naka-imbak na list.",
      loading: "Nilo-load ang rollout list",
      onNote: "Naka-on ang global switch, kaya may feature na ito ang bawat workspace. Magkakabisa ang list kapag in-off na ang switch.",
      readOnly: "Makikita ng role mo ang list na ito pero hindi mo ito mababago.",
      searchLabel: "Maghanap ng workspace",
      searchPlaceholder: "Maghanap ng workspace",
      searchHint: "Mag-type ng hindi bababa sa {{min}} letra ng pangalan ng workspace, slug o email ng may-ari.",
      searchResults: "Mga tugmang workspace",
      searchEmptyTitle: "Walang tugmang workspace",
      searchEmptyBody: "Tingnan ang spelling o subukan ang email address ng may-ari.",
      add: "Idagdag",
      addLabel: "Idagdag ang {{name}} sa rollout list",
      inList: "Nasa list na",
      listTitle: "Nasa rollout list",
      listCount: "{{used}} sa {{max}}",
      changes: "{{added}} idinagdag, {{removed}} inalis (hindi pa nase-save)",
      noChanges: "Walang pagbabago",
      emptyTitle: "Wala pang workspace",
      emptyBody: "Maghanap sa itaas at idagdag ang unang workspace. Hanggang noon, ang global switch lang ang nagpapasya.",
      emptyBodyReadOnly: "Walang idinagdag na workspace. Ang global switch lang ang nagpapasya.",
      removeLabel: "Alisin ang {{name}} sa rollout list",
      missingWorkspace: "Wala na ang workspace",
      unnamedWorkspace: "Hindi maipakita ang pangalan",
      namesLimited: "Hinahanap lang ang pangalan ng unang {{count}} workspace.",
      full: "Puno na ang list ({{max}} workspace). Mag-alis ng isa para makapagdagdag.",
      save: "I-save ang rollout list",
      saved: "Na-save ang rollout list ng {{name}}: {{count}} workspace",
      saved_other: "Na-save ang rollout list ng {{name}}: {{count}} na workspace",
    },
  },
  maintenance: {
    subtitle: "Ginagawang read-only ang buong platform. Para sa naka-iskedyul na window, gamitin ang",
    scheduleLink: "System Health → Mag-iskedyul ng maintenance",
    banner: "Mensahe ng banner",
    during: "Habang may maintenance",
    options: {
      keep_chat: "Patuloy na sumagot ang chat (mula sa replica)",
      pause_ingestion: "I-pause ang ingestion queues",
      widget_offline: "Ipakita ang offline na mensahe sa mga widget",
      allow_staff: "Payagang mag-sign in ang platform staff",
    },
    preview: "Preview ng banner (tenant console)",
    previewEmpty: "Dito lalabas ang mensahe ng banner mo.",
    confirmTitle: "I-on na ang maintenance mode?",
    confirmBody: "Magiging read-only agad ang lahat ng tenant console at ipapakita ang banner. Mapo-pause ang ingestion queues. Naka-log ito at aabisuhan ang on-call channel.",
    turnOn: "I-on",
    onToast: "NAKA-ON ang maintenance mode · naka-log bilang platform.maintenance.enabled",
    offToast: "Naka-off ang maintenance mode · normal na ulit ang mga tenant",
    liveLead: "NAKA-ON ang maintenance mode.",
    liveBody: "Read-only ang mga tenant console at ipinapakita ang maintenance banner.",
    noAccessTitle: "Kailangan ng system permission ang maintenance",
    noAccessBody: "Mga Super Admin at Platform Engineer lang ang puwedeng tumingin o magbago ng maintenance mode.",
  },
};
